import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { iconVariants } from '../IconVariants';
import { useStateBasedProps } from './useStateBasedProps';
import { IconProps, StateBasedProps } from '../Button/ButtonContent';

interface ButtonIconProps {
  icon?: string | StateBasedProps<string>;
  iconStyle?: 'filled' | 'outlined' | StateBasedProps<'filled' | 'outlined'>;
  iconScale?: number | StateBasedProps<number>;
  iconColor?: string | StateBasedProps<string>;
  iconProps?: IconProps | StateBasedProps<IconProps>;
  isHovered: boolean;
  hoverEnabled: boolean;
  hasMultipleItems: boolean;
}

export const ButtonIcon: React.FC<ButtonIconProps> = ({
  icon,
  iconStyle,
  iconScale,
  iconColor,
  iconProps,
  isHovered,
  hoverEnabled,
  hasMultipleItems,
}) => {
  const currentIconName = useStateBasedProps(
    typeof icon === 'object' ? icon : { default: icon || '' },
    isHovered,
    hoverEnabled,
  );

  const currentIconStyle = useStateBasedProps(
    typeof iconStyle === 'object' ? iconStyle : { default: iconStyle || 'outlined' },
    isHovered,
    hoverEnabled,
  );

  const currentIconScale = useStateBasedProps(iconScale, isHovered, hoverEnabled);
  const currentIconColor = useStateBasedProps(iconColor, isHovered, hoverEnabled);
  const currentIconProps = useStateBasedProps(iconProps, isHovered, hoverEnabled) || {};

  if (!currentIconName) return null;

  const iconVariant = iconVariants.find((v) => v.type === currentIconName);
  if (!iconVariant) return null;

  const SelectedIcon = currentIconStyle === 'filled' ? iconVariant.filled : iconVariant.outlined;
  if (!SelectedIcon) return null;

  const boxStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: currentIconProps.alignSelf
      ? currentIconProps.alignSelf === 'flex-start'
        ? 'flex-start'
        : currentIconProps.alignSelf === 'flex-end'
          ? 'flex-end'
          : 'center'
      : 'center',
    justifyContent: currentIconProps.justifySelf
      ? currentIconProps.justifySelf === 'flex-start'
        ? 'flex-start'
        : currentIconProps.justifySelf === 'flex-end'
          ? 'flex-end'
          : 'center'
      : 'center',
    padding: currentIconProps.padding ?? 0,
    flex: hasMultipleItems ? '0 0 auto' : '1',
    height: '100%',
  };

  return (
    <Box sx={boxStyle}>
      {React.cloneElement(SelectedIcon, {
        style: {
          fontSize: currentIconProps.fontSize || 24,
          color: currentIconProps.color || currentIconColor,
          margin: 0,
          verticalAlign: 'middle',
          display: 'inline-block',
          lineHeight: 1,
          transform: `scale(${currentIconScale})`,
          transition: 'transform 0.2s, color 0.2s',
          pointerEvents: 'none',
          ...(SelectedIcon.props as any)?.style,
        },
      })}
    </Box>
  );
};
